import React, { useState, useEffect } from "react";

import { SolutionsFrame } from "components/solutions/SolutionsFrame";
import { BlockStandard } from "components/admin/BlockStandard";
import {
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  TextField,
  Autocomplete,
} from "@mui/material";
import {
  CityResponse,
  useGreenEnergyContext,
} from "contexts/GreenEnergyContext";
import { fetchCitiesList } from "api/onboarding";
import { BuildingTypes } from "api/buildingTypes";
import { useToken } from "contexts/UserContext";
import { useSetError, useErrorInfo } from "contexts/ErrorContext";
import { CustomCodes } from "contexts/ErrorContextData";

const FRAME_DATA = {
  title: "Buscar melhores soluções",
  buttonText: "Continuar",
  nextScreen: "building-has-fv",
  skipTo: "building-has-fv",
};

const TYPE_OPTIONS: { label: string; imgSrc: string; type: BuildingTypes }[] = [
  {
    label: "Residência",
    type: "house",
    imgSrc: "/static/img/building-types/house.png",
  },
  {
    label: "Escola",
    type: "school",
    imgSrc: "/static/img/building-types/school.png",
  },
  {
    label: "Empresa",
    type: "enterprise",
    imgSrc: "/static/img/building-types/enterprise.png",
  },
  {
    label: "Condomínio",
    type: "condo",
    imgSrc: "/static/img/building-types/condo.png",
  },
  {
    label: "Outro",
    type: "other",
    imgSrc: "/static/img/building-types/other.png",
  },
];

const SX_STYLE = {
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 2,
  },
  cardStatus: {
    "&[data-active]": {
      border: (theme) => `3px solid ${theme.palette.primary.main}`,
    },
  },
};

export const BuildingType = ({ history }) => {
  const token = useToken();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [cities, setCities] = useState<CityResponse>([]);
  const [buildingType, setBuildingType] = useState<BuildingTypes>();
  const [buildingName, setBuildingName] = useState<string>("");
  const { city, setCity, setBuilding } = useGreenEnergyContext();
  const setGlobalError = useSetError();
  const errorInfo = useErrorInfo();
  const hasFormError = errorInfo?.code === CustomCodes.INCOMPLETE_FORM;

  useEffect(() => {
    const getCities = async () => {
      try {
        const data = await fetchCitiesList(token);
        setCities(data);
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      } finally {
        setIsLoading(false);
      }
    };
    getCities();
  }, [token, setGlobalError]);

  const handleButton = () => {
    if (buildingName && buildingType && city) {
      setCity(city);
      const building = { name: buildingName, type: buildingType };
      setBuilding((prevState) => ({ ...prevState, ...building }));
      history.push({
        pathname: `/onboarding/${FRAME_DATA.nextScreen}`,
        state: { from: "/onboarding/building-type" },
      });
    } else if (buildingName === "pular") {
      history.push({
        pathname: `/onboarding/${FRAME_DATA.nextScreen}`,
        state: { from: "/onboarding/building-type" },
      });
    } else {
      setGlobalError(CustomCodes.INCOMPLETE_FORM);
    }
  };

  return (
    <SolutionsFrame
      {...FRAME_DATA}
      onButtonClick={handleButton}
      isLoading={isLoading}
    >
      <BlockStandard hideDivider>
        <Typography variant="h1">
          Para qual o tipo de imóvel você está procurando solução?
        </Typography>
        <Typography>
          Marque uma das opções para cadastrar o imóvel que você busca
          descarbonizar e economizar
        </Typography>

        <Grid item container direction="column" sx={SX_STYLE.cardGrid}>
          {TYPE_OPTIONS.map(({ label, type, imgSrc }) => (
            <Card key={label} sx={{ borderRadius: "12px" }}>
              <CardActionArea
                data-active={buildingType === type || undefined}
                onClick={() => setBuildingType(type)}
                sx={SX_STYLE.cardStatus}
              >
                <CardMedia sx={{ display: "flex", justifyContent: "center" }}>
                  <img alt="" src={imgSrc} height="130" />
                </CardMedia>
                <CardContent>
                  <Typography variant="body2" align="center">
                    {label}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Grid>
        {hasFormError && !buildingType && (
          <Typography align="center" fontWeight="bold" color="error">
            É obrigatório selecionar um tipo de imóvel
          </Typography>
        )}

        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
          Qual cidade está localizada o imóvel?
        </Typography>
        <Autocomplete
          clearOnBlur
          value={city}
          getOptionLabel={({ city, sigla }) => `${city} - ${sigla}`}
          // @ts-expect-error return from MUI isn't properly typed
          onChange={(e, newValue) => setCity(newValue)}
          disabled={!cities?.length}
          loading={isLoading}
          loadingText="Carregando a lista de cidades..."
          options={cities}
          renderInput={(params) => (
            <TextField
              {...params}
              error={hasFormError && !city}
              variant="outlined"
            />
          )}
        />

        <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
          Insira um nome para identificar o imóvel
        </Typography>
        <TextField
          value={buildingName}
          onChange={({ target }) => setBuildingName(target.value)}
          error={hasFormError && !buildingName}
          variant="outlined"
        />
      </BlockStandard>
    </SolutionsFrame>
  );
};
